import {Component, OnDestroy, OnInit} from '@angular/core';
import {InstitutionService} from "../../core/services/institution.service";
import {getDefaultPageable, Pageable, PageParams} from "../../core/models/page.model";
import {IInstitution} from "../../core/models/institution.model";
import {customConfirmationDialog, customDynamicDialog, DEFAULT_ROWS_PER_PAGE_OPTIONS} from "../../core/utils/utils";
import {ToastService} from "../../core/services/toast.service";
import {TableModule} from "primeng/table";
import {DynamicTableComponent} from "../../shared/components/dynamic-table/dynamic-table.component";
import {CustomDialogData} from "../../core/models/custom-dialog.model";
import {InstitutionDetailsComponent} from "./institution-details/institution-details.component";
import {DialogService, DynamicDialogRef} from "primeng/dynamicdialog";
import {PaginatorState} from "primeng/paginator";
import {take} from "rxjs";
import {ToolbarComponent} from "../../shared/components/toolbar/toolbar.component";
import {CustomAction, CustomTableHeader} from "../../core/models/custom-table.model";
import {ModuleManagementComponent} from "./module-management/module-management.component";
import {ConfirmationService} from "primeng/api";
import {ConfirmDialogModule} from "primeng/confirmdialog";

@Component({
    selector: 'app-institution',
    imports: [
        // Modules
        TableModule,
        // Components
        DynamicTableComponent, ToolbarComponent,
        // Directives & Pipes
        ConfirmDialogModule
    ],
    templateUrl: './institution.component.html',
    styleUrl: './institution.component.scss'
})
export class InstitutionComponent implements OnInit, OnDestroy {

  institutions: IInstitution[] = [];
  columns: CustomTableHeader[] = [
    {
      key: 'name',
      column: 'Nom',
      type: 'text'
    },
    {
      key: 'phone',
      column: 'Téléphone',
      type: 'text'
    },
    {
      key: 'email',
      column: 'Email',
      type: 'text'
    },
    {
      key: 'type',
      column: 'Type',
      type: 'text'
    },
    {
      key: 'state',
      column: 'Etat',
      type: 'state'
    }
  ]
  pageable: PageParams = {page: 0, first: 0, rows: 0, totalRecords: 0, rowPerPageOptions: []};
  ref: DynamicDialogRef | null = null;
  actions: CustomAction[] = [];
  toolbarActions: CustomAction[] = [];

  constructor(
    private readonly institutionService: InstitutionService,
    private readonly toastService: ToastService,
    public readonly dialogService: DialogService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit() {
    this.toolbarActions = [
      {
        role: 'CREATE_INSTITUTION',
        icon: 'pi pi-plus',
        label: 'Nouveau',
        severity: 'primary',
        emit: () => this.createInstitution(),
        visible: true
      }
    ];
    this.loadInstitutions();
    this.actions = this.getActions();
  }

  ngOnDestroy() {
    this.ref?.close();
  }

  getActions(): CustomAction[] {
    return [
      {
        role: 'READ_INSTITUTION',
        icon: 'pi pi-info-circle',
        label: 'Consulter',
        severity: 'info',
        emit: (item) => this.viewOrEditInstitution(item),
        visible: true
      },
      {
        role: 'UPDATE_INSTITUTION',
        icon: 'pi pi-pen-to-square',
        label: 'Modifier',
        severity: 'primary',
        emit: (item) => this.viewOrEditInstitution(item, true),
        visible: true
      },
      {
        role: 'UPDATE_INSTITUTION',
        icon: 'pi pi-cog',
        label: 'Modules',
        severity: 'primary',
        emit: (item) => this.viewOrEditModules(item, true),
        visible: true
      },
      {
        role: 'DELETE_INSTITUTION',
        icon: 'pi pi-trash',
        label: 'Supprimer',
        severity: 'danger',
        emit: (item) => this.confirmDeletion(item),
        visible: true
      }
    ];
  }

  loadInstitutions(pageable: Pageable = getDefaultPageable()) {
    this.institutionService.getInstitutionByDeleted(false, pageable).subscribe({
      next: page => {
        this.institutions = page.content;
        this.pageable = {
          page: page.number,
          first: page.number * page.size,
          rows: page.size,
          totalRecords: page.totalElements,
          rowPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS
        }
      },
      error: err => {
        this.toastService.showToast('Erreur lors du chargement des institutions', err.error, 'error');
      }
    })
  }

  viewOrEditModules(institution: IInstitution, isView: boolean = true) {
    const data = { isView, data: institution } as CustomDialogData;
    this.ref = this.dialogService.open(ModuleManagementComponent, customDynamicDialog('Gestion des modules', data));
    this.ref?.onClose.pipe(take(1)).subscribe(() => this.loadInstitutions());
  }

  createInstitution() {
    const data = {isCreate: true} as CustomDialogData;
    this.ref = this.dialogService.open(InstitutionDetailsComponent, customDynamicDialog('Création d\'institution', data));
    this.ref?.onClose.pipe(take(1)).subscribe(() => this.loadInstitutions());
  }

  viewOrEditInstitution(institution: IInstitution, isEdit: boolean = false) {
    const data = {isView: !isEdit, isEdit, data: institution} as CustomDialogData;
    this.ref = this.dialogService.open(InstitutionDetailsComponent, customDynamicDialog('Détails d\'institution', data));
    this.ref?.onClose.pipe(take(1)).subscribe(() => this.loadInstitutions());
  }

  confirmDeletion(item: any) {
    console.log(item)
    const header = 'Suppression d\'une institution';
    const message = `Voulez-vous supprimer l'institution << <strong> ${item.name} </strong> >> ?` ;
    const acceptLabel = 'Oui';
    const rejectLabel = 'Annuler';
    const options = customConfirmationDialog(header, message, {accept: () => this.deleteInstitution(item)}, acceptLabel, rejectLabel)

    this.confirmationService.confirm(options);
  }

  deleteInstitution(institution: IInstitution) {
    this.institutionService.deleteInstitution(institution.id).subscribe({
      next: () => {
        this.toastService.showToast('Institution supprimée', `L'institution ${institution.name} a été supprimée avec succès.`);
        this.loadInstitutions();
      },
      error: err => {
        this.toastService.showToast('Erreur lors de la suppression de l\'institution', err.error, 'error');
      }
    });
  }

  toggleInstitution(institution: IInstitution) {
    this.institutionService.toggleInstitution(institution.id).subscribe({
      next: () => {
        this.toastService.showToast('Institution modifiée', `L'institution ${institution.name} a été modifiée avec succès.`);
        this.loadInstitutions();
      },
      error: err => {
        this.toastService.showToast('Erreur lors de la modification de l\'institution', err.error, 'error');
      }
    });
  }

  onPageChange = (event: PaginatorState) => this.loadInstitutions(getDefaultPageable(event.page, event.rows));

}
